import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Landmark, Camera, CheckCircle2, ArrowRight, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { sportsApi, financeApi, errMsg } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { Logo, UserAvatar, Money } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ADHESION = 15;
const FEE = 0.73; // 15*0.029+0.30 rounded (display only)

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [sports, setSports] = useState([]);
  const [step, setStep] = useState("form"); // form | payment | done
  const [busy, setBusy] = useState(false);
  const [reference, setReference] = useState("");

  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", password: "", phone: "",
    birth_date: "", gender: "Masculin", address: "",
    emergency_contact_name: "", emergency_contact_phone: "", photo: null,
    sports: [], consent_image: false, consent_rules: false, adhesion_method: "card",
  });

  useEffect(() => { sportsApi.list().then((r) => setSports(r.data)).catch(() => {}); }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleSport = (id) => setF((p) => ({
    ...p, sports: p.sports.includes(id) ? p.sports.filter((s) => s !== id) : [...p.sports, id],
  }));

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Image trop lourde (max 10 Mo)"); return; }
    try {
      const photo = await compressImage(file);
      set("photo", photo);
    } catch { toast.error("Impossible de traiter l'image"); }
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!f.consent_image || !f.consent_rules) { toast.error("Les deux consentements sont obligatoires."); return; }
    if (f.sports.length === 0) { toast.error("Choisissez au moins une section sportive."); return; }
    if (f.password.length < 6) { toast.error("Mot de passe : minimum 6 caractères."); return; }
    setBusy(true);
    try {
      await register(f);
      toast.success("Inscription enregistrée. Passons au paiement de l'adhésion.");
      setStep("payment");
    } catch (err) {
      toast.error(errMsg(err, "Inscription échouée"));
    } finally { setBusy(false); }
  };

  const payCard = async () => {
    setBusy(true);
    try {
      const { data } = await financeApi.checkout({ purpose: "adhesion", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  const payInterac = async () => {
    if (!reference.trim()) { toast.error("Entrez votre référence de virement."); return; }
    setBusy(true);
    try {
      await financeApi.interac({ purpose: "adhesion", reference });
      toast.success("Référence Interac soumise.");
      setStep("done");
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-muted/40">
      <div className="asl-hero-gradient asl-noise">
        <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
          <Link to="/"><Logo size={44} light /></Link>
          <h1 className="mt-4 font-heading text-3xl font-bold text-white">Inscription à l'ASL</h1>
          <p className="mt-1 text-white/70">Rejoignez la communauté Sports et Loisirs de Pierre-De-Saurel.</p>
          <div className="mt-5 flex items-center gap-2 text-xs font-medium text-white/80">
            {["Informations", "Adhésion (15$)", "Confirmation"].map((s, i) => {
              const active = (step === "form" && i === 0) || (step === "payment" && i === 1) || (step === "done" && i === 2);
              const done = (step === "payment" && i === 0) || (step === "done" && i <= 1);
              return (
                <React.Fragment key={s}>
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 ${active ? "bg-secondary text-white" : done ? "bg-white/20 text-white" : "bg-white/10 text-white/50"}`}>
                    {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="h-4 w-4 rounded-full bg-white/30 text-center text-[10px] leading-4">{i + 1}</span>}
                    {s}
                  </span>
                  {i < 2 && <span className="h-px w-4 bg-white/20" />}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        {step === "form" && (
          <form onSubmit={submitForm} className="space-y-6">
            <Card><CardContent className="space-y-4 p-5">
              <h3 className="font-heading font-semibold">Identité</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5"><Label>Prénom *</Label><Input required value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="inscription-prenom-input" /></div>
                <div className="space-y-1.5"><Label>Nom *</Label><Input required value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="inscription-nom-input" /></div>
                <div className="space-y-1.5"><Label>Date de naissance (JJ/MM) *</Label><Input required placeholder="15/06" value={f.birth_date} onChange={(e) => set("birth_date", e.target.value)} data-testid="inscription-naissance-input" /></div>
                <div className="space-y-1.5"><Label>Genre *</Label>
                  <Select value={f.gender} onValueChange={(v) => set("gender", v)}>
                    <SelectTrigger data-testid="inscription-genre-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Masculin">Masculin</SelectItem><SelectItem value="Féminin">Féminin</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent></Card>

            <Card><CardContent className="space-y-4 p-5">
              <h3 className="font-heading font-semibold">Coordonnées</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5"><Label>Courriel *</Label><Input required type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="inscription-email-input" /></div>
                <div className="space-y-1.5"><Label>Mot de passe *</Label><Input required type="password" value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="inscription-password-input" /></div>
                <div className="space-y-1.5"><Label>Téléphone *</Label><Input required value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="inscription-tel-input" /></div>
                <div className="space-y-1.5"><Label>Adresse *</Label><Input required value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="inscription-adresse-input" /></div>
                <div className="space-y-1.5"><Label>Contact d'urgence *</Label><Input required value={f.emergency_contact_name} onChange={(e) => set("emergency_contact_name", e.target.value)} data-testid="inscription-urgence-nom-input" /></div>
                <div className="space-y-1.5"><Label>Tél. contact d'urgence *</Label><Input required value={f.emergency_contact_phone} onChange={(e) => set("emergency_contact_phone", e.target.value)} data-testid="inscription-urgence-tel-input" /></div>
              </div>
            </CardContent></Card>

            <Card><CardContent className="space-y-4 p-5">
              <h3 className="font-heading font-semibold">Photo de profil</h3>
              <div className="flex items-center gap-4">
                {f.photo ? <img src={f.photo} alt="aperçu" className="h-20 w-20 rounded-full object-cover" /> : <div className="flex h-20 w-20 items-center justify-center rounded-full bg-muted"><Camera className="h-7 w-7 text-muted-foreground" /></div>}
                <label className="cursor-pointer rounded-lg border bg-white px-4 py-2 text-sm font-medium hover:bg-muted">
                  Choisir une photo
                  <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="inscription-photo-input" />
                </label>
              </div>
            </CardContent></Card>

            <Card><CardContent className="space-y-3 p-5">
              <h3 className="font-heading font-semibold">Sections sportives *</h3>
              <div className="grid gap-2 sm:grid-cols-2">
                {sports.map((s) => (
                  <label key={s.id} className={`flex cursor-pointer items-center gap-2 rounded-lg border p-3 ${f.sports.includes(s.id) ? "border-secondary bg-secondary/5" : ""}`}>
                    <Checkbox checked={f.sports.includes(s.id)} onCheckedChange={() => toggleSport(s.id)} data-testid={`inscription-sport-${s.name}`} />
                    <span className="text-sm font-medium">{s.name}</span>
                  </label>
                ))}
              </div>
            </CardContent></Card>

            <Card><CardContent className="space-y-4 p-5">
              <h3 className="font-heading font-semibold">Consentements</h3>
              <label className="flex items-start gap-3 text-sm">
                <Checkbox checked={f.consent_image} onCheckedChange={(v) => set("consent_image", !!v)} data-testid="inscription-consentement-image-checkbox" />
                <span>J'autorise l'ASL à prendre et partager ma photo/mon image dans le cadre des activités de l'association (droit à l'image).</span>
              </label>
              <label className="flex items-start gap-3 text-sm">
                <Checkbox checked={f.consent_rules} onCheckedChange={(v) => set("consent_rules", !!v)} data-testid="inscription-consentement-reglements-checkbox" />
                <span>Je m'engage à respecter les statuts et règlements de l'association.</span>
              </label>
            </CardContent></Card>

            <Card><CardContent className="space-y-3 p-5">
              <h3 className="font-heading font-semibold">Méthode de paiement de l'adhésion (15$ CAD)</h3>
              <RadioGroup value={f.adhesion_method} onValueChange={(v) => set("adhesion_method", v)} className="grid gap-3 sm:grid-cols-2">
                <label className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 ${f.adhesion_method === "card" ? "border-secondary bg-secondary/5" : ""}`}>
                  <RadioGroupItem value="card" data-testid="inscription-methode-carte" /><CreditCard className="h-4 w-4" /> Carte bancaire
                </label>
                <label className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 ${f.adhesion_method === "interac" ? "border-secondary bg-secondary/5" : ""}`}>
                  <RadioGroupItem value="interac" data-testid="inscription-methode-interac" /><Landmark className="h-4 w-4" /> Virement Interac
                </label>
              </RadioGroup>
            </CardContent></Card>

            <Button type="submit" size="lg" disabled={busy} className="w-full bg-secondary hover:bg-emerald-600 text-white" data-testid="inscription-soumettre-bouton">
              {busy ? "Enregistrement…" : "Continuer vers le paiement"} <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
            <p className="text-center text-sm text-muted-foreground">Déjà membre ? <Link to="/connexion" className="text-secondary hover:underline">Connexion</Link></p>
          </form>
        )}

        {step === "payment" && (
          <div className="space-y-5">
            <Card><CardContent className="p-5">
              <h3 className="font-heading text-lg font-semibold">Frais d'adhésion</h3>
              <p className="text-sm text-muted-foreground">Payables une seule fois à l'inscription.</p>
              <div className="mt-4 space-y-2 rounded-xl bg-muted/50 p-4 text-sm">
                <div className="flex justify-between"><span>Adhésion</span><Money value={ADHESION} /></div>
                {f.adhesion_method === "card" && <div className="flex justify-between text-muted-foreground"><span>Frais de traitement</span><Money value={FEE} /></div>}
                <div className="flex justify-between border-t pt-2 font-semibold"><span>Total</span><Money value={f.adhesion_method === "card" ? ADHESION + FEE : ADHESION} className="text-secondary" /></div>
              </div>
            </CardContent></Card>

            <RadioGroup value={f.adhesion_method} onValueChange={(v) => set("adhesion_method", v)} className="grid gap-3 sm:grid-cols-2">
              <label className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 ${f.adhesion_method === "card" ? "border-secondary bg-secondary/5" : ""}`}><RadioGroupItem value="card" /><CreditCard className="h-4 w-4" /> Carte bancaire</label>
              <label className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 ${f.adhesion_method === "interac" ? "border-secondary bg-secondary/5" : ""}`}><RadioGroupItem value="interac" /><Landmark className="h-4 w-4" /> Virement Interac</label>
            </RadioGroup>

            {f.adhesion_method === "card" ? (
              <Button size="lg" disabled={busy} onClick={payCard} className="w-full bg-secondary hover:bg-emerald-600 text-white" data-testid="adhesion-payer-carte-bouton">
                <CreditCard className="mr-1.5 h-4 w-4" /> {busy ? "Redirection…" : "Payer par carte"}
              </Button>
            ) : (
              <Card><CardContent className="space-y-3 p-5">
                <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
                  Effectuez un virement Interac de <b>15,00 $ CAD</b> à :<br />
                  Courriel : <b>informations.slps@gmail.com</b><br />
                  Mot de passe (réponse) : <b>adhesion</b><br />
                  Puis entrez votre numéro/référence de virement ci-dessous.
                </div>
                <div className="space-y-1.5"><Label>Référence de virement *</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} data-testid="adhesion-interac-reference-input" placeholder="Ex : ABC123456" /></div>
                <Button disabled={busy} onClick={payInterac} className="w-full bg-secondary hover:bg-emerald-600 text-white" data-testid="adhesion-payer-interac-bouton">{busy ? "Envoi…" : "Soumettre la référence"}</Button>
              </CardContent></Card>
            )}
            <button onClick={() => navigate("/tableau-de-bord")} className="w-full text-center text-sm text-muted-foreground hover:underline" data-testid="adhesion-plus-tard">Payer plus tard</button>
          </div>
        )}

        {step === "done" && (
          <Card><CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <div className="rounded-full bg-amber-100 p-4"><Clock className="h-8 w-8 text-amber-600" /></div>
            <h3 className="font-heading text-xl font-semibold">Demande en attente de validation</h3>
            <p className="max-w-md text-sm text-muted-foreground">Votre référence Interac a été soumise. Un administrateur validera votre paiement et votre compte sous peu. Vous recevrez une notification.</p>
            <Button onClick={() => navigate("/tableau-de-bord")} className="mt-2 bg-secondary hover:bg-emerald-600 text-white" data-testid="adhesion-done-dashboard">Aller au tableau de bord</Button>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
